import { createTheme, darken } from "@mui/material/styles";

// The page body fill in the v2 School layout.
const LAYOUT_BODY_BG = "#F1F2F2";

// The shared "glass" container surface: a near-white gradient fill, an inset
// rim and a 1px gradient ring. Exported on its own (and hung off
// `theme.custom.glassBox` below) so components that already hold a theme can
// spread `theme.custom.glassBox`, while components without one - memoized rows,
// module-scope sx constants - can import this directly.
export const glassBox = {
  position: "relative",
  borderRadius: 4,
  background: "linear-gradient(135deg, #FFFFFF 50%, #F1F2F2 100%)",
  // Inset rim mimics a glass edge lit from the bottom-right: white glow on
  // the bottom/right inner edges, a darker one on the top/left.
  boxShadow: [
    "inset -6px -6px 8px 0px #FFFFFFCC",
    "inset 6px 6px 8px 0px #00000008",
  ].join(", "),
  // Gradient border: white on the top/left edges fading to a darker gray on
  // the bottom/right. Painted as a 1px ring via a masked pseudo-element so
  // it follows the border radius (border-image can't round corners). The
  // two masks - one clipped to the content box, one to the full border box -
  // are excluded from each other, leaving only the 1px padding band visible.
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit",
    padding: "1px",
    background:
      "linear-gradient(135deg, #FFFFFF 50%, rgba(0, 0, 0, 0.12) 100%)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    pointerEvents: "none",
  },
};

// The brand angular gradient: blue -> teal -> green -> teal -> blue swept once
// around the element's center. The stop angles come straight from the design
// token (green 119.42deg, teal 225.86deg, blue 332.31deg, teal 405.86deg), and
// the sweep closes on the color it opened with, so there is no seam where the
// circle wraps. Exported for the same reason as glassBox above.
export const brandGradient = [
  "conic-gradient(from 119.42deg at 50% 50%,",
  "#2DAE61 0deg,",
  "#1BA39D 106.44deg,",
  "#0898D9 212.89deg,",
  "#1BA39D 286.44deg,",
  "#2DAE61 360deg)",
].join(" ");

// Drops CardContent's own padding (including the extra bottom padding it adds
// as a last child) so a table inside a glass card runs edge to edge - the cells
// carry their own padding, and a gutter on top of that leaves the rows visibly
// inset from the frame. Sections that are not tables (a header row, a stacked
// mobile list) put their own padding back.
export const flushCardContent = {
  p: 0,
  "&:last-child": { pb: 0 },
};

export const themeOptions = {
  typography: {
    fontFamily: "halcom, sans-serif",
  },
  palette: {
    primary: {
      main: "#0395D3", // Blue
      dark: "#0277A3",
    },
    secondary: {
      main: "#A836D0", // Purple
    },
    gray: {
      main: "#666666",
    },
  },
  components: {
    // Menus and Selects render a Popover, whose default scroll lock puts
    // `overflow: hidden` on <body> while open. With the page header pinned via
    // `position: sticky` on the window scroll, that lock collapses the scroll
    // context: the sticky header drops out of view when a dropdown opens and the
    // page snaps to the top when it closes. Disabling the lock keeps the window
    // scroll (and the sticky header) intact. Dialogs use Modal directly, not
    // Popover, so they keep their own scroll lock (background stays put).
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: "0px 4px 17.6px 0px #00000013",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        textColorPrimary: {
          "&.Mui-selected": {
            color: "inherit",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "#0395D3",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // Right-anchored drawers sit flush against the screen edge: square
        // the right corners on desktop so the paper doesn't show a rounded
        // gap. Mobile drawers are full-width and keep the default.
        paper: ({ theme, ownerState }) => ({
          ...(ownerState.anchor === "right" && {
            [theme.breakpoints.up("md")]: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }),
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 20,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          // On mobile every dialog fills the screen (full-screen sheet): no
          // margin, no max constraints, square corners. Mirrors MUI's
          // `fullScreen` prop so we don't have to set it per-dialog.
          [theme.breakpoints.down("sm")]: {
            margin: 0,
            width: "100%",
            maxWidth: "100%",
            height: "100%",
            maxHeight: "none",
            borderRadius: 0,
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // Keep scrolling inside the dialog: once the content reaches its
          // edge the gesture stops here instead of chaining to the page behind.
          overscrollBehavior: "contain",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 10,
        },
      },
    },
  },
  custom: {
    // Shared layout surfaces. The chrome (navbar + fixed sidebar) is the body
    // gray a step darker, so the two read as one chromatic family instead of
    // two unrelated grays: same hue, the chrome just recessed ~5%.
    layoutBg: {
      body: LAYOUT_BODY_BG,
      chrome: darken(LAYOUT_BODY_BG, 0.05),
      // Warning wash for the navbar while a staff member is viewing a school
      // year other than the school's active one. Loud on purpose: it is the
      // only cue that edits are landing outside the live year.
      inactiveYear: "#EF6C00",
    },
    glassBox,
    brandGradient,
  },
};

export default createTheme(themeOptions);
